import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Container, Col, Row, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import moment from 'moment';
import ConfirmationModal from '../../common_components/ConfirmationModal';
import { getCurrencySymbol } from '../../common_components/choices';
import { getItemMeasure } from '../services';

const ProductPriceModal = ({ show, handleClose, productPrice = null, product = null, refreshProductPrices }) => {
    console.log('Product: ', product);

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedStore, setSelectedStore] = useState(null);
    const [price, setPrice] = useState('');
    const [priceCurrency, setPriceCurrency] = useState('EUR');
    const [dateTime, setDateTime] = useState('');
    const [source, setSource] = useState('');
    const [sourceType, setSourceType] = useState(''); // New state for dropdown selection
    const [sourceUrl, setSourceUrl] = useState('');
    const [isValid, setIsValid] = useState(true);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    // Get the user info from local storage
    const userInfoContent = JSON.parse(localStorage.getItem('userInfo'));
    const token = localStorage.getItem('token');

    const resetForm = useCallback(() => {
        setPrice('');
        setPriceCurrency(userInfoContent.preferred_currency || 'XXX');
        setSource('');
        setSourceType('');
        setSourceUrl('');
        setDateTime(moment().format('YYYY-MM-DDTHH:mm'));
        setIsValid(true);
        setSelectedProduct(null);
        setSelectedStore(null);
    }, [userInfoContent.preferred_currency]);

    useEffect(() => {
        if (productPrice) {
            setPrice(productPrice.price);
            setPriceCurrency(productPrice.price_currency);
            setSource(productPrice.source);
            setSourceType(['store_shelf', 'online'].includes(productPrice.source) ? productPrice.source : 'Others');
            setSourceUrl(productPrice.source_url);
            setDateTime(productPrice.date_time ? moment(productPrice.date_time).format('YYYY-MM-DDTHH:mm') : '');
            setIsValid(productPrice.is_valid);
            setSelectedStore(productPrice.store ? {
                value: productPrice.store.id,
                label: productPrice.store.name,
            } : null);
        } else {
            resetForm();
        }
    }, [productPrice, resetForm]);

    useEffect(() => {
        if (product) {
            setSelectedProduct({
                value: product.id,
                label: `${product.name} ${getItemMeasure(product)}`,
            });
            console.log('Product set to: ', {
                value: product.id,
                label: `${product.name} ${getItemMeasure(product)}`,
            });
        } else {
            setSelectedProduct(null);
        }
    }, [product]);

    const fetchProducts = async (inputValue) => {
        if (inputValue.length < 3) return []; // Only fetch if input length is at least 3
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-products/?search=${inputValue}`, {
                headers: {
                    'Authorization': `Token ${token}`,
                },
            });
            return response.data.results.map(product => ({
                value: product.id,
                label: `${product.product_brand.name} - ${product.name} ${getItemMeasure(product)}`,
            }));
        } catch (error) {
            console.error('Error fetching products:', error);
            return [];
        }
    };

    const fetchStores = async (inputValue) => {
        if (inputValue.length < 3) return []; // Only fetch if input length is at least 3
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-stores/?search=${inputValue}`, {
                headers: {
                    'Authorization': `Token ${token}`,
                },
            });
            return response.data.results.map(store => ({
                value: store.id,
                label: store.name,
            }));
        } catch (error) {
            console.error('Error fetching stores:', error);
            return [];
        }
    };

    // using shared getItemMeasure from services

    const handleProductSelectChange = (selectedOption) => {
        setSelectedProduct(selectedOption);
    };

    const handleStoreSelectChange = (selectedOption) => {
        setSelectedStore(selectedOption);
    };

    const handleInputChange = (setter) => (e) => {
        setter(e.target.value);
    };

    const handleSourceTypeChange = (e) => {
        const value = e.target.value;
        setSourceType(value);
        if (value !== 'Others') {
            setSource(value);
        } else {
            setSource('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!price || !dateTime || !selectedProduct || !selectedStore || !source) {
            toast.error('Price, product, store, source, and dates are required.');
            return;
        }

        const payload = {
            product_id: selectedProduct ? selectedProduct.value : (product ? product.id : null),
            store_id: selectedStore ? selectedStore.value : (productPrice?.store?.id || null),
            price: parseFloat(price),
            price_currency: priceCurrency,
            source,
            source_url: sourceUrl,
            date_time: moment(dateTime).toISOString(),
            is_valid: isValid,
        };

        console.log('Payload:', payload);

        try {
            if (productPrice) {
                await axios.patch(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-prices/${productPrice.id}/`, payload, {
                    headers: {
                        'Authorization': `Token ${token}`
                    }
                });
                toast.success('Product price updated successfully!');
            } else {
                await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-prices/`, payload, {
                    headers: {
                        'Authorization': `Token ${token}`
                    }
                });
                toast.success('Product price added successfully!');
            }

            try {
                await refreshProductPrices();
            } catch (error) {
                console.error('Error refreshing product prices:', error);
            }

            handleClose();
        } catch (error) {
            toast.error('Failed to save product price. Please try again.');
            console.error('Error saving product price:', error);
        }
    };


    const handleDelete = async () => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-prices/${productPrice.id}/`, {
                headers: {
                    'Authorization': `Token ${token}`
                }
            });

            toast.success('Product price deleted successfully!');
            closeDeleteConfirmation(); // Close the confirmation modal
            refreshProductPrices();
            handleClose(); // Close the main modal
        } catch (error) {
            toast.error('Failed to delete product price. Please try again.');
            console.error('Error deleting product price:', error);
        }
    };

    const showDeleteConfirmation = () => {
        setShowConfirmationModal(true);
    };

    const closeDeleteConfirmation = () => {
        setShowConfirmationModal(false);
    };

    const isEditable = !productPrice || productPrice?.is_owner;

    return (
        <Container>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {productPrice ? `Edit Price for ${selectedProduct ? selectedProduct.label : 'Unknown Product'}` : `Add Price for ${selectedProduct ? selectedProduct.label : 'Unknown Product'}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Product</Form.Label>
                                <AsyncSelect
                                    value={selectedProduct}
                                    loadOptions={fetchProducts}
                                    onChange={handleProductSelectChange}
                                    placeholder="Select a product"
                                    cacheOptions
                                    defaultOptions
                                    isDisabled={!isEditable}
                                />
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Store</Form.Label>
                                <AsyncSelect
                                    value={selectedStore}
                                    loadOptions={fetchStores}
                                    onChange={handleStoreSelectChange}
                                    placeholder="Select a store"
                                    cacheOptions
                                    defaultOptions
                                    isDisabled={!isEditable}
                                />
                            </Form.Group>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Price</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>{getCurrencySymbol(priceCurrency)}</InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            placeholder="Enter price"
                                            value={price}
                                            onChange={handleInputChange(setPrice)}
                                            required
                                            readOnly={!isEditable}
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date and Time</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        placeholder="Enter date and time"
                                        value={dateTime}
                                        onChange={handleInputChange(setDateTime)}
                                        required
                                        readOnly={!isEditable}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Source</Form.Label>
                                <Form.Control as="select" value={sourceType} onChange={handleSourceTypeChange} required disabled={!isEditable}>
                                    <option value="" disabled>Select source</option>
                                    <option value="store_shelf">Store Shelf</option>
                                    <option value="online">Online</option>
                                    <option value="Others">Others</option>
                                </Form.Control>
                                {sourceType === 'Others' && (
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter source"
                                        value={source}
                                        onChange={handleInputChange(setSource)}
                                        required
                                        readOnly={!isEditable}
                                        style={{ marginTop: '10px' }}
                                    />
                                )}
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Source URL</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter source URL (optional)"
                                    value={sourceUrl}
                                    onChange={handleInputChange(setSourceUrl)}
                                    readOnly={!isEditable}
                                />
                            </Form.Group>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Is Valid"
                                checked={isValid}
                                onChange={(e) => setIsValid(e.target.checked)}
                                disabled={!isEditable}
                            />
                        </Form.Group>
                        {isEditable && (
                            <>
                                <Button variant="primary" type="submit">
                                    {productPrice ? 'Update Price' : 'Add Price'}
                                </Button>
                                {productPrice && productPrice.is_owner && (
                                    <Button variant="danger" onClick={showDeleteConfirmation} style={{ marginLeft: '10px' }}>
                                        Delete Price
                                    </Button>
                                )}
                            </>
                        )}
                    </Form>
                </Modal.Body>
            </Modal>
            <ConfirmationModal
                show={showConfirmationModal}
                onHide={closeDeleteConfirmation}
                onConfirm={handleDelete}
                title="Remove this product price?"
                body="Are you sure you want to remove this product price?"
            />
        </Container>
    );
};

export default ProductPriceModal;
